import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { StyleProvider } from '@ant-design/cssinjs';
import { ConfigProvider, theme } from 'antd-v6';
import { getGlassComponents, glassToken } from './glassTheme';
import { getMuiComponents, getMuiToken, muiWaveEffect } from './muiTheme';
import './glass.css';
import './mui.css';

type ThemeMode = 'antd' | 'mui' | 'glass' | 'custom1';

interface ThemeContextType {
	darkMode: boolean;
	setDarkMode: (value: boolean) => void;
	primaryColor: string;
	setPrimaryColor: (value: string) => void;
	styleMode: ThemeMode;
	setStyleMode: (value: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = () => {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error('useThemeContext must be used within a ThemeProvider');
	}
	return context;
};

interface ThemeProviderProps {
	children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
	const [darkMode, setDarkMode] = useState<boolean>(false);
	const [primaryColor, setPrimaryColor] = useState<string>(darkMode ? '#ffffff' : '#010101ff');
	const [styleMode, setStyleMode] = useState<ThemeMode>('antd');

	useEffect(() => {
		const root = document.documentElement;
		const isDark = darkMode;

		const colors = {
			primary: isDark ? '#ffffff' : primaryColor,
			primaryHover: isDark ? lightenColor('#ffffff', 20) : lightenColor('#1890ff', 20),
			bgBase: isDark ? '#121212' : '#f5f7fa',
			bgContainer: isDark ? '#1e1e1e' : '#ffffff',
			bgElevated: isDark ? '#2a2a2a' : '#ffffff',
			textBase: isDark ? '#ffffff' : '#1c1b1f',
			textSecondary: isDark ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.45)',
			textTertiary: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.25)',
			textQuaternary: isDark ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.15)',
			borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : '#e8e8e8',
			borderSplit: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f0f0f0',
			shadowColor: isDark ? 'rgba(0, 0, 0, 0.45)' : 'rgba(0, 0, 0, 0.08)',
			headerBg: isDark ? '#010101ff' : '#ffffff',
			siderBg: isDark ? '#1e1e1e' : '#ffffff',
			siderText: isDark ? '#ffffff' : '#010101ff',
			itemHoverBg: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(230, 230, 230, 0.61)',
			itemSelectedBg: isDark ? 'rgba(255, 255, 255, 0.15)' : '#c0c0c05c',
			submenuBg: isDark ? '#2a2a2a' : 'rgba(0, 0, 0, 0.04)',
			formLabel: isDark ? 'rgba(255, 255, 255, 0.57)' : '#c6c6c65c',
			TimeLineMargin: isDark ? '#ffffff' : '#595959',
		};

		Object.entries(colors).forEach(([key, value]) => {
			const cssVarName = `--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
			root.style.setProperty(cssVarName, value);
		});

		root.style.setProperty('--sider-bg', colors.siderBg);
		root.style.setProperty('--sider-text', colors.siderText);

		root.style.setProperty('--bg-color', colors.bgBase);
		root.style.setProperty('--container-bg', colors.bgContainer);
		root.style.setProperty('--text-color', colors.textBase);
		root.style.setProperty('--text-secondary', colors.textSecondary);
		root.style.setProperty('--form-label-color', colors.formLabel);
		root.style.setProperty('--border-color', colors.borderColor);
		root.style.setProperty('--primary-color', colors.primary);
		root.style.setProperty('--primary-hover-color', colors.primaryHover);

		if (isDark) {
			document.body.classList.add('dark');
			document.body.classList.remove('light');
		} else {
			document.body.classList.add('light');
			document.body.classList.remove('dark');
		}

		if (styleMode === 'glass') {
			root.style.setProperty('--glass-bg', isDark ? '#2a2a2a' : 'rgba(255, 255, 255, 0.45)');
			root.style.setProperty('--glass-border', isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.3)');
			root.style.setProperty('--glass-blur', '20px');
			root.style.setProperty('--glass-saturate', '180%');
			root.style.setProperty(
				'--glass-shadow',
				isDark ? '0 8px 32px 0 rgba(0, 0, 0, 0.37)' : '0 8px 32px 0 rgba(31, 38, 135, 0.15)'
			);
			root.style.setProperty(
				'--glass-highlight',
				isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.4)'
			);
		}
	}, [darkMode, styleMode, primaryColor]);

	useEffect(() => {
		document.body.classList.remove('antd', 'mui', 'glass');
		document.body.classList.add(styleMode);
	}, [styleMode]);

	const getThemeConfig = () => {
		const isMui = styleMode === 'mui';
		const baseToken = {
			colorPrimary: darkMode ? '#ffffff' : primaryColor,
			borderRadius: isMui ? 4 : 6,
			fontFamily: isMui
				? "'Roboto', 'Helvetica', 'Arial', sans-serif"
				: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
			colorText: darkMode ? 'rgba(255, 255, 255, 0.85)' : '#1c1b1f',
			colorTextBase: darkMode ? '#ffffff' : '#000000',
		};

		const muiComponents = isMui ? getMuiComponents(darkMode, primaryColor) : {};

		const customComponents =
			styleMode === 'custom1'
				? {
						Input: {
							borderRadius: 12,
							controlHeight: 44,
							colorBgContainer: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
							activeBorderColor: primaryColor,
							hoverBorderColor: primaryColor,
						},
						Button: {
							controlHeight: 44,
						},
				  }
				: {};

		const customToken =
			styleMode === 'custom1'
				? {
						margin: 20,
						marginSM: 16,
						marginLG: 28,
						borderRadius: 16,
						borderRadiusXS: 4,
						borderRadiusSM: 8,
						borderRadiusLG: 24,
						boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
						boxShadowSecondary: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
				  }
				: {};

		const glassTokenOverrides = styleMode === 'glass' ? glassToken : {};
		const muiTokenOverrides = isMui ? getMuiToken(darkMode, primaryColor) : {};
		const glassComponents = styleMode === 'glass' ? getGlassComponents(darkMode) : {};

		return {
			algorithm: darkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
			token: {
				...baseToken,
				...customToken,
				...glassTokenOverrides,
				...muiTokenOverrides,
				...(darkMode
					? {
							colorBgBase: '#121212',
							colorBgContainer: '#1e1e1e',
							colorBgElevated: '#1e1e1e',
							colorBgLayout: '#121212',
							colorText: 'rgba(255, 255, 255, 0.85)',
							colorTextBase: 'rgba(255, 255, 255, 0.85)',
					  }
					: {
							colorBgContainer: '#ffffff',
							colorBgElevated: '#ffffff',
					  }),
			},
			components: {
				...muiComponents,
				...customComponents,
				...glassComponents,
				Menu: {
					darkItemBg: darkMode ? '#1e1e1e' : '#001529',
					darkSubMenuItemBg: darkMode ? '#1e1e1e' : '#001529',
					darkItemColor: darkMode ? '#ffffff' : 'rgba(255, 255, 255, 0.65)',
					darkItemSelectedBg: primaryColor,
					darkItemSelectedColor: '#ffffff',
					darkItemHoverColor: '#ffffff',
				},
				Dropdown: {
					colorBgElevated: darkMode ? '#1e1e1e' : '#ffffff',
				},
				Modal: {
					colorBgElevated: darkMode ? '#1e1e1e' : '#ffffff',
				},
				Table: {
					colorText: darkMode ? '#ffffff' : '#1e1e1e',
					colorBgContainer: darkMode ? '#232323ff' : '#ffffff',
					headerBg: darkMode ? 'rgba(53, 52, 52, 1)' : '#f5f7fa',
					rowSelectedBg: darkMode ? 'rgba(255, 255, 255, 0.15)' : '#c0c0c05c',
					rowSelectedHoverBg: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(230, 230, 230, 0.61)',
				},
				Checkbox: {
					colorWhite: darkMode ? '#010101ff' : '#ffffff',
				},
				Switch: {
					colorPrimary: darkMode ? '#9c9c9c' : '#000000',
					colorTextQuaternary: darkMode ? '#000000' : '#9c9c9c',
					colorTextTertiary: '#cfcfcf',
					colorPrimaryHover: darkMode ? '#cfcfcf' : '#9c9c9c',
				},
			},
		};
	};

	return (
		<ThemeContext.Provider
			value={{ darkMode, setDarkMode, primaryColor, setPrimaryColor, styleMode, setStyleMode }}
		>
			<StyleProvider hashPriority="high">
				<ConfigProvider
					prefixCls="ant6"
					theme={getThemeConfig()}
					wave={styleMode === 'mui' ? muiWaveEffect : undefined}
				>
					{children}
				</ConfigProvider>
			</StyleProvider>
		</ThemeContext.Provider>
	);
};

function lightenColor(col: string, amt: number) {
	let usePound = false;
	if (col[0] === '#') {
		col = col.slice(1);
		usePound = true;
	}
	const num = parseInt(col, 16);
	let r = (num >> 16) + amt;
	if (r > 255) r = 255;
	else if (r < 0) r = 0;
	let b = ((num >> 8) & 0x00ff) + amt;
	if (b > 255) b = 255;
	else if (b < 0) b = 0;
	let g = (num & 0x0000ff) + amt;
	if (g > 255) g = 255;
	else if (g < 0) g = 0;
	return (usePound ? '#' : '') + (g | (b << 8) | (r << 16)).toString(16).padStart(6, '0');
}
